import { useEffect, useState } from "react";
import { api } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import {
  Search,
  Loader2,
  Check,
  X,
  Crown,
  CheckCircle2,
  Store,
  PauseCircle,
  PlayCircle,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_TABS = [
  { key: "pending", label: "Pendientes", tone: "amber" },
  { key: "approved", label: "Aprobados", tone: "emerald" },
  { key: "rejected", label: "Rechazados", tone: "red" },
  { key: "suspended", label: "Suspendidos", tone: "neutral" },
  { key: "paused", label: "Pausados", tone: "neutral" },
];

const CATEGORY_LABELS = {
  restaurantes: "Restaurantes",
  hoteles: "Hoteles",
  cafes: "Cafés",
  bares: "Bares",
  tours: "Tours",
  car_wash: "Car Wash",
  mecanicos: "Mecánicos",
  salud_belleza: "Salud y Belleza",
  entretenimiento: "Entretenimiento",
  otros: "Otros",
};

export default function AdminGuiaTab() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [actingId, setActingId] = useState(null);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { status, limit: 100 };
      if (query.trim()) params.q = query.trim();
      const { data } = await api.get("/admin/guia/businesses", { params });
      setItems(data.items || []);
      setCounts(data.status_counts || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar la lista");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleAction = async (biz, action, reason = null) => {
    setActingId(biz.business_id);
    try {
      const body = { action };
      if (reason) body.reason = reason;
      await api.post(`/admin/guia/businesses/${biz.business_id}/action`, body);
      toast.success(`Acción aplicada: ${action}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo aplicar la acción");
    } finally {
      setActingId(null);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget || !rejectReason.trim()) {
      toast.error("Motivo de rechazo requerido");
      return;
    }
    await handleAction(rejectTarget, "reject", rejectReason.trim());
    setRejectTarget(null);
    setRejectReason("");
  };

  return (
    <div className="space-y-4" data-testid="admin-guia-tab">
      {/* Status tabs */}
      <div className="flex gap-2 flex-wrap" data-testid="admin-guia-status-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setStatus(t.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
              status === t.key
                ? "bg-[#111111] text-white"
                : "bg-white border border-neutral-200 hover:bg-neutral-50"
            }`}
            data-testid={`admin-guia-status-${t.key}`}
          >
            {t.label}
            <Badge className="bg-neutral-200 text-neutral-700 border-0">{counts[t.key] || 0}</Badge>
          </button>
        ))}
      </div>

      {/* Search */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
        className="flex gap-2"
      >
        <div className="flex-1 flex items-center gap-2 px-3 bg-white rounded-xl border border-neutral-200">
          <Search className="w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Buscar por nombre, ciudad o email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="border-0 focus-visible:ring-0 shadow-none px-0"
            data-testid="admin-guia-search"
          />
        </div>
        <Button type="submit" variant="outline">Buscar</Button>
      </form>

      {/* List */}
      {loading ? (
        <div className="py-16 text-center" data-testid="admin-guia-loading">
          <Loader2 className="w-6 h-6 animate-spin text-terracotta mx-auto" />
        </div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center bg-white rounded-2xl border border-dashed border-neutral-300" data-testid="admin-guia-empty">
          <Store className="w-10 h-10 text-neutral-300 mx-auto mb-2" />
          <p className="text-neutral-700 font-medium">Sin resultados en "{STATUS_TABS.find((t) => t.key === status)?.label}"</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-guia-list">
          {items.map((biz) => (
            <li
              key={biz.business_id}
              className="card-base flex gap-4 flex-wrap items-start"
              data-testid={`admin-guia-item-${biz.business_id}`}
            >
              <div className="w-20 h-20 rounded-xl overflow-hidden bg-neutral-100 flex-shrink-0">
                {biz.photos?.[0] ? (
                  <img src={biz.photos[0]} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-neutral-400">
                    <Store className="w-6 h-6" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h3 className="font-heading font-semibold text-agave">{biz.name}</h3>
                  {biz.featured && (
                    <Badge className="bg-amber-500 text-white border-0 gap-1">
                      <Crown className="w-3 h-3" /> Destacado
                    </Badge>
                  )}
                  {biz.verified_partner && (
                    <Badge className="bg-emerald-600 text-white border-0 gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Verificado
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-neutral-500 mb-1">
                  {CATEGORY_LABELS[biz.category] || biz.category} · {biz.city} · {biz.discount_percent ? `-${Math.round(biz.discount_percent)}% descuento` : "sin descuento"}
                </p>
                <p className="text-sm text-neutral-700 line-clamp-2">{biz.description}</p>
                <p className="text-xs text-neutral-500 mt-1">
                  Dueño: {biz.owner_name} · {biz.email}
                </p>
                {biz.rejection_reason && status !== "approved" && (
                  <div className="mt-2 text-xs text-red-700 bg-red-50 rounded-lg px-2 py-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {biz.rejection_reason}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 w-full sm:w-auto sm:flex-row sm:flex-wrap" data-testid={`admin-guia-actions-${biz.business_id}`}>
                {biz.status === "pending" && (
                  <>
                    <Button
                      size="sm"
                      onClick={() => handleAction(biz, "approve")}
                      disabled={actingId === biz.business_id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`admin-guia-approve-${biz.business_id}`}
                    >
                      {actingId === biz.business_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                      Aprobar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setRejectTarget(biz)}
                      disabled={actingId === biz.business_id}
                      className="text-red-600 hover:bg-red-50 border-red-200"
                      data-testid={`admin-guia-reject-${biz.business_id}`}
                    >
                      <X className="w-3.5 h-3.5 mr-1" />
                      Rechazar
                    </Button>
                  </>
                )}
                {biz.status === "approved" && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAction(biz, biz.featured ? "unfeature" : "feature")}
                      disabled={actingId === biz.business_id}
                      data-testid={`admin-guia-feature-${biz.business_id}`}
                    >
                      <Crown className="w-3.5 h-3.5 mr-1" />
                      {biz.featured ? "Quitar destacado" : "Destacar"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAction(biz, biz.verified_partner ? "unverify" : "verify")}
                      disabled={actingId === biz.business_id}
                      data-testid={`admin-guia-verify-${biz.business_id}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                      {biz.verified_partner ? "Quitar verificación" : "Verificar"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAction(biz, "suspend")}
                      disabled={actingId === biz.business_id}
                      className="text-red-600 hover:bg-red-50 border-red-200"
                      data-testid={`admin-guia-suspend-${biz.business_id}`}
                    >
                      <PauseCircle className="w-3.5 h-3.5 mr-1" />
                      Suspender
                    </Button>
                  </>
                )}
                {biz.status === "suspended" && (
                  <Button
                    size="sm"
                    onClick={() => handleAction(biz, "unsuspend")}
                    disabled={actingId === biz.business_id}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`admin-guia-unsuspend-${biz.business_id}`}
                  >
                    <PlayCircle className="w-3.5 h-3.5 mr-1" />
                    Reactivar
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent data-testid="admin-guia-reject-dialog">
          <DialogHeader>
            <DialogTitle>Rechazar listado</DialogTitle>
            <DialogDescription>
              Comparte un motivo claro y accionable. Será enviado por email al dueño.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            placeholder="Ej. La foto principal no muestra el local. Sube una foto del exterior."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            data-testid="admin-guia-reject-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>
              Cancelar
            </Button>
            <Button
              onClick={confirmReject}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="admin-guia-reject-confirm"
            >
              Enviar rechazo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
